import {useEffect, useRef, useState} from 'react';

import {isInJupyterNotebook} from '../../setup';
import {useDispatch} from '../../state/hooks';
import * as PollingActions from '../../state/polling/actions';
import {type RunHistoryKeyInfo} from '../../types/run';
import * as RunHelpers from '../../util/runhelpers';

export function useBasePollInterval({
  runState,
  runHistoryKeys,
  autoRefreshEnabled,
}: {
  runState?: string;
  runHistoryKeys?: RunHistoryKeyInfo;
  autoRefreshEnabled: boolean;
}) {
  const [pollInterval, setPollInterval] = useState(0);
  const pollStartTime = useRef(0);
  const dispatch = useDispatch();

  useEffect(() => {
    let newPollInterval = autoRefreshEnabled ? pollInterval : 0;

    // Runs in Jupyter notebooks can be resumed, so we want to poll even if
    // the run is not in the running state.
    if (
      (runState === 'running' || isInJupyterNotebook()) &&
      runHistoryKeys != null
    ) {
      const queryWeight = RunHelpers.queryWeight(runHistoryKeys);
      newPollInterval = queryWeight / 5;

      let minPI = 30000;
      if (new Date().getTime() - pollStartTime.current < 20 * 60 * 1000) {
        // If less than 20 minutes on page, use a smaller minimum
        minPI = 2000;
      }

      if (newPollInterval < minPI) {
        newPollInterval = minPI;
      }

      const maxPI = 90 * 1000; // 90 seconds
      if (newPollInterval > maxPI) {
        newPollInterval = maxPI;
      }
    }

    if (autoRefreshEnabled && newPollInterval !== pollInterval) {
      setPollInterval(newPollInterval);
      // It's ugly that we compute all this poll interval stuff in the run and group
      // page itself. This computation is now legacy, but it still works.
      // TODO: move smart poll interval logic into runs-low/sagas.ts
      dispatch(PollingActions.setBasePollInterval(newPollInterval));
    }
  }, [
    autoRefreshEnabled,
    pollInterval,
    setPollInterval,
    dispatch,
    runState,
    runHistoryKeys,
  ]);

  return pollInterval;
}
